import { postJson } from '@lib/api/utils/post-json';
import { matchesFileSignature } from '@lib/doc-vault/file-requests/matches-file-signature';
import {
  MAX_UPLOAD_SIZE_BYTES,
  formatMaxUploadSize,
} from '@lib/shared/constants/max-upload-size';
import { chunk } from '@lib/shared/utils/chunk';
import { mapWithConcurrency } from '@lib/shared/utils/map-with-concurrency';

import { FLOW_UPLOAD_MIME } from '../constants/flow-upload';
import { UploadRejectedError } from '../errors/upload-rejected-error';
import { FLOW_UPLOAD_BATCH_MAX } from '../schemas/flow-upload-schema';

/** Parallel signed-URL PUTs; bytes go to Storage, never through our functions. */
const PUT_CONCURRENCY = 4;

interface BeginResponse {
  files: { storagePath: string; uploadUrl: string; fileName: string }[];
}

const INVALID_PDF_MESSAGE =
  'This document must be a PDF. Please convert it and try again.';

async function assertPdfMagicBytes(file: File): Promise<void> {
  const head = await file.slice(0, 8).arrayBuffer();
  if (!matchesFileSignature(FLOW_UPLOAD_MIME, head)) {
    throw new UploadRejectedError(INVALID_PDF_MESSAGE, 400);
  }
}

/** Map Supabase Storage PUT failures to user-facing copy. The response body is
 *  untrusted; only known error shapes are translated — everything else falls
 *  back to a generic message while preserving HTTP status for telemetry. */
async function readStoragePutErrorMessage(response: Response): Promise<string> {
  let body: unknown = null;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    body = await response.json().catch(() => null);
  } else {
    const text = await response.text().catch(() => '');
    if (text) {
      try {
        body = JSON.parse(text) as unknown;
      } catch {
        body = null;
      }
    }
  }

  const record =
    body && typeof body === 'object'
      ? (body as { error?: unknown; message?: unknown })
      : null;
  const errorCode =
    typeof record?.error === 'string' ? record.error.toLowerCase() : '';
  const message =
    typeof record?.message === 'string' ? record.message.toLowerCase() : '';

  // Match MIME allowlist failures only — avoid mapping unrelated "not supported"
  // Storage errors to the PDF conversion message after we already sent PDF bytes.
  // Codes are lowercased CamelCase (InvalidMimeType → invalidmimetype).
  if (errorCode.includes('mime') || message.includes('mime type')) {
    return INVALID_PDF_MESSAGE;
  }
  if (
    errorCode.includes('alreadyexists') ||
    message.includes('already exists')
  ) {
    return 'This upload conflicted with an existing file. Please try again.';
  }
  if (errorCode.includes('nocontent') || message.includes('no content')) {
    return 'This PDF appears to be empty. Please choose a different file.';
  }
  if (errorCode.includes('invalidkey') || message.includes('invalid key')) {
    return "This filename contains characters we can't store. Please rename the file or remove special punctuation and try again.";
  }

  return `Upload failed (HTTP ${response.status}).`;
}

/** UX guard only; the bucket, `begin`, and the processing worker re-check. */
export async function validateFlowUploadFile(file: File): Promise<void> {
  // Check type before size so an oversized non-PDF gets the type-conversion
  // guidance rather than a size error that wrongly calls it a PDF.
  // Accept by extension too: browsers report an empty/non-standard file.type
  // for some PDFs (no extension mapping, certain drag-drop sources).
  const looksPdf =
    file.type === FLOW_UPLOAD_MIME || file.name.toLowerCase().endsWith('.pdf');
  if (!looksPdf) {
    throw new UploadRejectedError(INVALID_PDF_MESSAGE, 400);
  }
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    const sizeMb = Math.round(file.size / (1024 * 1024));
    throw new UploadRejectedError(
      `This PDF is ${sizeMb} MB. Signing documents must be under ${formatMaxUploadSize()}.`,
      413
    );
  }
  await assertPdfMagicBytes(file);
}

/**
 * Stages PDFs for an existing deal/package. `begin` (≤10 per call, chunks in
 * parallel), then signed-URL PUTs. Callers validate first so a bad file fails
 * before side effects. Returns accept-shaped refs in selection order.
 */
export async function stageFlowDocumentFiles({
  dealId,
  packageId,
  flowId,
  files,
  onUrlsMinted,
}: {
  dealId: string;
  packageId: string;
  flowId?: string;
  files: readonly File[];
  /** Fires once every signed URL is minted, before any PUT (keeps the 'parse' stage order). */
  onUrlsMinted?: () => void;
}): Promise<Array<{ storagePath: string; name: string }>> {
  const targets = (
    await Promise.all(
      chunk(files, FLOW_UPLOAD_BATCH_MAX).map(batch =>
        postJson<BeginResponse>('/api/flows/upload-document-v2/begin', {
          dealId,
          packageId,
          ...(flowId && { flowId }),
          // Always declare PDF: the PUT sends application/pdf and the bucket
          // enforces it, so an empty browser file.type must not fail validation.
          files: batch.map(file => ({
            name: file.name,
            size: file.size,
            contentType: FLOW_UPLOAD_MIME,
          })),
        })
      )
    )
  ).flatMap(response => response.files);
  if (targets.length !== files.length) {
    throw new Error('Upload URL was not returned.');
  }
  onUrlsMinted?.();

  return mapWithConcurrency(
    files,
    PUT_CONCURRENCY,
    async (file, index, signal) => {
      const target = targets[index]!;
      // PUT hits Supabase (not our API), so surface its status rather than the
      // shared API-error reader. ArrayBuffer (not File) so the browser cannot
      // substitute file.type into Content-Type; the bucket allowlist rejects
      // application/octet-stream.
      const putRes = await fetch(target.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': FLOW_UPLOAD_MIME },
        body: await file.arrayBuffer(),
        // A failed sibling aborts the rest: the batch is all-or-nothing.
        signal,
      });
      if (!putRes.ok) {
        // Every branch of readStoragePutErrorMessage returns copy we authored.
        throw new UploadRejectedError(
          await readStoragePutErrorMessage(putRes),
          putRes.status
        );
      }
      return { storagePath: target.storagePath, name: target.fileName };
    }
  );
}
